import renderInputField from 'components/FormControls/textInput'
import { Button, Icon, Tooltip } from 'gu-ui-kit'
import omit from 'lodash/omit'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, Form, formValueSelector, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import * as Yup from 'yup'
import initialAdvertiserInfo from 'modules/Forms/Account/Create/initialAdvertiserInfo'
import advertiserInfoValidationRulesExtended from 'modules/Forms/Account/Create/subcomponents/advertiser-info-validation-rules-extended'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/SubmitButtons'
import AdvertiserInfoGroup from 'modules/Forms/Account/Create/subcomponents/AdvertiserInfoGroup'
import SERVICES_API_V1 from 'api/services/v1'
import emailValidator from 'utils/validators/emailValidator'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import formStyles from 'modules/Forms/common-form.module.scss'
import clsx from 'clsx'
import { isRequiredField } from 'utils/isRequiredField'

export const TargetMailSchema = () =>
  Yup.object()
    .shape({
      logins: Yup.array().of(Yup.object()),
      login: Yup.string().when('logins', ([logins], schema) =>
        logins
          ? schema.strip()
          : schema
              .test('baseLoginTest', '', baseLoginValidator)
              .required('common::validation_messages.required'),
      ),
      network: Yup.string().required('common::validation_messages.required'),
      mytarget_user_id: Yup.string().nullable(),
      mytarget_username: Yup.string()
        .test(
          'emailValidator',
          'common::validation_messages.incorrect_email_format',
          emailValidator,
        )
        .required('common::validation_messages.required'),
    })
    .concat(advertiserInfoValidationRulesExtended())

let CreateTargetMailAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  networkType,
  formHeader,
  form,
  networkId,
  array,
  multiLogins,
  submitFailed,
}) => {
  let childWindow
  const selector = formValueSelector(form)
  const { t } = useTranslation([])
  const submitAccountData = useAccountCreateFormSubmit({ form, onClose, multiLogins, networkType })

  const breakpoint = useBreakpoint()

  const {
    formValues: { advertiser_info, mytarget_username },
  } = useSelector((state) => ({
    formValues: selector(state, 'mytarget_username', 'advertiser_info'),
  }))

  global.getBack = useCallback(
    ({ username, user_id }) => {
      !!childWindow && childWindow.close()
      childWindow = null

      autofill('mytarget_user_id', user_id)
      autofill('mytarget_username', username)
    },
    [childWindow, autofill],
  )

  useEffect(() => {
    if (networkId) {
      autofill('network', networkId)
    }
  }, [networkId])

  const submit = useCallback(
    (values) => submitAccountData(omit(values, ['advertiser_template'])),
    [submitAccountData],
  )

  const openConnectWindow = useCallback(() => {
    childWindow = global.open(
      '',
      'Target Mail oAuth dialog',
      'resizable,toolbar=yes,location=yes,scrollbars=yes,menubar=yes,width=800,height=600,top=0,left=0',
    )
    SERVICES_API_V1.getNetworkAuthUrl({ networkType })
      .then(({ response: { data = {} } = {} }) => {
        if (!childWindow) {
          return
        }
        const { url = null } = data
        if (url) {
          childWindow.location = url
          childWindow.focus()
        } else {
          childWindow.close()
        }
      })
      .catch(() => {
        if (childWindow) {
          childWindow.close()
        }
      })
  }, [childWindow, networkType])

  const ButtonTargetMailUserName = (props) => (
    <Button
      noWrap
      variant={'ghost'}
      link
      type="button"
      onClick={openConnectWindow}
      {...props}
    >
      {t('services::forms.register_account.myTarget.fields.mytarget_username.get_url_label')}
    </Button>
  )

  return (
    <>
      {formHeader}

      <Form
        onSubmit={handleSubmit(submit)}
        noValidate
      >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.myTarget.fields.login.label'}
          />
        </div>

        <div className={'margin-bottom-16'}>
          <div className={formStyles['row_with-tooltip']}>
            <Field
              name="mytarget_username"
              required={isRequiredField(TargetMailSchema(networkType), 'mytarget_username')}
              fullWidth
              component={renderInputField}
              t={t}
              label={t('services::forms.register_account.myTarget.fields.mytarget_username.label')}
              InputProps={{
                endAdornment: !mytarget_username && !breakpoint.sm && (
                  <ButtonTargetMailUserName size={'m'} />
                ),
              }}
            />

            <div className={formStyles['tooltip-container']}>
              <Tooltip
                title={t(
                  'services::forms.register_account.myTarget.fields.mytarget_username.tooltip',
                )}
                placement="top"
                arrow
                tooltipClassName={formStyles['tooltip']}
              >
                <Icon
                  name={'information'}
                  color={'Label-Secondary'}
                  size={'m'}
                />
              </Tooltip>
            </div>
          </div>

          {!mytarget_username && breakpoint.sm && (
            <div className={'margin-top-8'}>
              <ButtonTargetMailUserName size={'s'} />
            </div>
          )}
        </div>

        <Field
          hidden
          name="mytarget_user_id"
          fullWidth
          component={renderInputField}
          label={t('services::forms.register_account.myTarget.fields.mytarget_user_id.label')}
          t={t}
        />

        <AdvertiserInfoGroup
          form={form}
          advertiser_info={advertiser_info}
          networkType={networkType}
          submitFailed={submitFailed}
        />

        <SubmitButtons
          formName={form}
          submitted={submitAccountData.submitted}
          submitting={submitting}
          onClose={onClose}
        />
      </Form>
    </>
  )
}

CreateTargetMailAccountForm = reduxForm({
  form: 'CreateTargetMailAccountForm',
  initialValues: {
    login: '',
    network: '',
    mytarget_user_id: null,
    mytarget_username: '',
    payment_profile_id: undefined,
    advertiser_info: initialAdvertiserInfo,
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateTargetMailAccountForm)

CreateTargetMailAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  valid: PropTypes.bool,
  pristine: PropTypes.bool,
  autofill: PropTypes.func,
  change: PropTypes.func,
  networkType: PropTypes.string,
  form: PropTypes.string,
  networkId: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  initialValues: PropTypes.object,
  formHeader: PropTypes.node,
  submitFailed: PropTypes.bool,
}

export default CreateTargetMailAccountForm
